import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { erc20Abi, maxUint256, parseEther, zeroAddress, type Address } from "viem";
import { getWalletClient } from "wagmi/actions";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { strikeOptionsAbi } from "../abi";
import { postOrder, useApiConfig, useMarket, useOptionChain, type ApiConfig, type ChainSeries } from "../lib/api";
import { robinhood } from "../lib/config";
import { fmtEth, fmtExpiry, fmtMcap, fmtNum, fmtUsd, timeLeft, wei } from "../lib/format";
import { byExpiry, expiryKind, fmtProb, probItm, shortDate } from "../lib/options";
import { connectedAccount, publicClient, sendTx } from "../lib/tx";
import { friendlyError } from "../lib/errors";
import { toast } from "../lib/toast";
import { wagmiConfig } from "../lib/wagmi";
import { ConnectButton } from "../components/Connect";
import { useWallet } from "../lib/wallet";
import { demoSign, demoWriterState, isDemo } from "../lib/demo";
import "./portfolio.css";
import "./trade.css";
import "./earn.css";

const wethAbi = [{ type: "function", name: "deposit", stateMutability: "payable", inputs: [], outputs: [] }] as const;

const orderTypes = {
  Order: [
    { name: "maker", type: "address" },
    { name: "taker", type: "address" },
    { name: "seriesId", type: "uint256" },
    { name: "side", type: "uint8" },
    { name: "amount", type: "uint256" },
    { name: "price", type: "uint256" },
    { name: "premiumToken", type: "address" },
    { name: "minSpot", type: "uint256" },
    { name: "maxSpot", type: "uint256" },
    { name: "deadline", type: "uint64" },
    { name: "nonce", type: "uint256" },
  ],
} as const;

const FEE = 0.1; // share of every premium that goes to holders

type Row = { s: ChainSeries; perM: number; yieldPct: number; apr: number; prob: number };

/** What selling one option on this series earns, per unit of collateral, at the vault's price. */
function rowFor(s: ChainSeries, spot: number, sigma: number): Row {
  const days = Math.max(1 / 24, (s.expiry - Date.now() / 1000) / 86_400);
  const collateral = s.isCall ? spot : s.strikeEth; // ETH value locked per option
  const yieldPct = ((s.askEth * (1 - FEE)) / collateral) * 100;
  return { s, perM: s.askEth * 1e6, yieldPct, apr: (yieldPct * 365) / days, prob: probItm(s, spot, sigma) };
}

const pct = (v: number) => (v >= 100 ? `${Math.round(v)}%` : v >= 10 ? `${v.toFixed(1)}%` : `${v.toFixed(2)}%`);

export function Earn() {
  const conn = useWallet();
  const { data: cfg } = useApiConfig();
  const { data: m } = useMarket();
  const { data: chain } = useOptionChain();
  const [isCall, setIsCall] = useState(true);
  const [expiry, setExpiry] = useState<number>();
  const [seriesId, setSeriesId] = useState<string>();
  const [size, setSize] = useState("1000000");
  const [pricePerM, setPricePerM] = useState<string>();
  const [busy, setBusy] = useState<string>();
  const qc = useQueryClient();

  const spot = m?.priceEth ?? 0;
  const eu = m?.ethUsd ?? 0;
  const sigma = chain?.sigma ?? 1.5;
  const series = useMemo(() => chain?.series ?? [], [chain]);

  // Headline yield per strategy: a ~1.25x call / ~0.8x put about a week out.
  const headline = useMemo(() => {
    const pick = (call: boolean) => {
      const now = Date.now() / 1000;
      const week = now + 7 * 86_400;
      const target = call ? 1.25 : 0.8;
      const score = (x: ChainSeries) => Math.abs(x.expiry - week) / 86_400 + Math.abs(x.moneyness - target) * 10;
      const best = series.filter((x) => x.isCall === call && x.expiry - now > 2 * 86_400).sort((a, b) => score(a) - score(b))[0];
      return best && spot > 0 ? rowFor(best, spot, sigma).apr : undefined;
    };
    return { call: pick(true), put: pick(false) };
  }, [series, spot, sigma]);

  const grouped = useMemo(() => byExpiry(series.filter((s) => s.isCall === isCall)), [series, isCall]);
  const expiries = [...grouped.keys()];
  // Sellers mostly compare weeklies: start on the expiry closest to a week out.
  const week = Date.now() / 1000 + 7 * 86_400;
  const nearWeek = expiries.slice().sort((a, b) => Math.abs(a - week) - Math.abs(b - week))[0];
  const exp = expiry && grouped.has(expiry) ? expiry : nearWeek;
  // Covered calls above spot, puts below: the strikes you would actually want to sell.
  const rows = (exp ? grouped.get(exp)! : [])
    .filter((s) => (isCall ? s.strikeEth >= spot * 0.95 && s.strikeEth <= spot * 2.6 : s.strikeEth <= spot * 1.05 && s.strikeEth >= spot * 0.4))
    .sort((a, b) => (isCall ? a.strikeEth - b.strikeEth : b.strikeEth - a.strikeEth))
    .map((s) => rowFor(s, spot, sigma));
  const target = isCall ? 1.25 : 0.8;
  const popular = rows.slice().sort((a, b) => Math.abs(a.s.moneyness - target) - Math.abs(b.s.moneyness - target))[0];
  const sel = rows.find((r) => r.s.id === seriesId) ?? popular;
  const s = sel?.s;

  const state = useWriterState(cfg, conn.address, isCall);
  const balance = wei(state.data?.strikeBalance ?? 0n);
  const n = Math.max(0, Math.floor(Number(size || 0)));
  const amount = parseEther(String(n));
  const suggested = s ? s.askEth * 1_000_000 : 0;
  const perM = pricePerM !== undefined && pricePerM !== "" ? Number(pricePerM) : suggested;
  const premiumEth = (perM / 1_000_000) * n;
  const netEth = premiumEth * (1 - FEE);
  const collateralEth = s ? (isCall ? spot * n : s.strikeEth * n) : 0;
  const days = s ? Math.max(1 / 24, (s.expiry - Date.now() / 1000) / 86_400) : 1;
  const yieldPct = collateralEth > 0 ? (netEth / collateralEth) * 100 : 0;
  const strikeLbl = s ? fmtMcap(s.strikeEth, eu) : "—";
  const tone = isCall ? "call" : "put";

  function pickSide(call: boolean) {
    setIsCall(call);
    setSeriesId(undefined);
    setPricePerM(undefined);
  }

  async function run() {
    if (!cfg || !s || !conn.address || n === 0) return;
    const token = cfg.contracts.token as Address;
    const options = cfg.contracts.options;
    try {
      const me = await connectedAccount();
      if (isCall) {
        if ((state.data?.strikeAllowance ?? 0n) < amount) {
          setBusy("Approving $STRIKE…");
          await sendTx("Approve $STRIKE", { address: token, abi: erc20Abi, functionName: "approve", args: [options, maxUint256] });
        }
      } else {
        const need = BigInt(Math.ceil(collateralEth * 1e18));
        const weth = state.data?.wethBalance ?? 0n;
        if (weth < need) {
          setBusy("Wrapping ETH…");
          await sendTx("Wrap ETH", { address: cfg.contracts.weth, abi: wethAbi, functionName: "deposit", value: need - weth });
        }
        if ((state.data?.wethAllowance ?? 0n) < need) {
          setBusy("Approving WETH…");
          await sendTx("Approve WETH", { address: cfg.contracts.weth, abi: erc20Abi, functionName: "approve", args: [options, maxUint256] });
        }
      }
      if (!state.data?.isOperator) {
        setBusy("Authorizing the exchange…");
        await sendTx("Let the exchange fill your orders", {
          address: options,
          abi: strikeOptionsAbi,
          functionName: "setWriteOperator",
          args: [cfg.contracts.exchange, true],
        });
      }

      setBusy("Sign the order in your wallet…");
      const order = {
        maker: me,
        taker: zeroAddress,
        seriesId: BigInt(s.id),
        side: 0,
        amount,
        price: parseEther((perM / 1_000_000).toFixed(18)),
        premiumToken: cfg.contracts.weth,
        minSpot: 0n,
        maxSpot: 0n,
        deadline: BigInt(s.expiry - 600),
        nonce: BigInt(Date.now()),
      };
      const signature = isDemo()
        ? await demoSign()
        : await (await getWalletClient(wagmiConfig, { chainId: robinhood.id })).signTypedData({
            account: me,
            domain: { name: "StrikeExchange", version: "1", chainId: robinhood.id, verifyingContract: cfg.contracts.exchange },
            types: orderTypes,
            primaryType: "Order",
            message: order,
          });
      await postOrder(
        Object.fromEntries(Object.entries(order).map(([k, v]) => [k, typeof v === "bigint" ? v.toString() : v])) as never,
        signature,
      );
      toast.show("ok", `Listed ${fmtNum(n)} ${isCall ? "covered calls" : "puts"}. Collateral is only locked when someone buys.`);
      qc.invalidateQueries({ queryKey: ["orders"] });
      state.refetch();
    } catch (err) {
      toast.show("err", friendlyError(err));
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <section className="wrap page earn">
      <header className="page-head">
        <span className="eyebrow">Earn</span>
        <h1>Sell options. Get paid up front.</h1>
        <p>
          Buyers pay you a premium in ETH the moment your order fills. Your collateral stays yours until someone buys, and $STRIKE locked in
          covered calls keeps earning holder rewards.
        </p>
      </header>

      <div className="strats">
        <Strategy
          on={isCall}
          tone="call"
          name="Covered call"
          apr={headline.call}
          lock="Lock $STRIKE · APR for a 1-week, +25% strike"
          text="Earn ETH on the $STRIKE you hold. If it runs past your strike, you sell at that price."
          onClick={() => pickSide(true)}
        />
        <Strategy
          on={!isCall}
          tone="put"
          name="Cash-secured put"
          apr={headline.put}
          lock="Lock ETH · APR for a 1-week, −20% strike"
          text="Earn ETH while you wait. If $STRIKE dips below your strike, you buy it at that price."
          onClick={() => pickSide(false)}
        />
      </div>

      {!chain?.open ? (
        <div className="panel empty">
          <p>Options open when $STRIKE graduates on pons.</p>
        </div>
      ) : (
        <div className="earn-grid">
          <div className="panel earn-build">
            <div className="eb-sec">
              <div className="eb-label">
                <span className="eb-n">1</span> Expiry
              </div>
              <div className="expiries">
                {expiries.map((e) => (
                  <button key={e} className={e === exp ? "on" : ""} onClick={() => (setExpiry(e), setSeriesId(undefined), setPricePerM(undefined))}>
                    <span>{shortDate(e)}</span>
                    <span className="mono">
                      {timeLeft(e).split(" ")[0]}
                      {expiryKind(e) !== "daily" && ` · ${expiryKind(e)}`}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="eb-sec">
              <div className="eb-label">
                <span className="eb-n">2</span> Strike
                <span className="eb-hint mono">market cap now {fmtUsd(spot * 1e9 * eu)}</span>
              </div>
              <div className={`st st-${tone}`} role="table">
                <div className="st-head mono" role="row">
                  <span>Strike</span>
                  <span>Premium / 1M</span>
                  <span>Yield</span>
                  <span>APR</span>
                  <span title="Model chance the option ends in the money and your collateral is used">Assigned</span>
                </div>
                {rows.map((r) => (
                  <button
                    key={r.s.id}
                    className={`st-row ${r.s.id === s?.id ? "on" : ""}`}
                    role="row"
                    onClick={() => (setSeriesId(r.s.id), setPricePerM(undefined))}
                  >
                    <span className="st-k">
                      <span className="num">{fmtMcap(r.s.strikeEth, eu)}</span>
                      <span className="num faint">
                        {r.s.strikeEth > spot ? "+" : ""}
                        {Math.round((r.s.strikeEth / spot - 1) * 100)}%
                      </span>
                      {r === popular && <span className="st-pop">popular</span>}
                    </span>
                    <span className="num">{fmtEth(r.perM, 4).replace(" ETH", "")}</span>
                    <span className="num">{pct(r.yieldPct)}</span>
                    <span className={`num st-apr ${tone}`}>{pct(r.apr)}</span>
                    <span className="num mute">{fmtProb(r.prob)}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="eb-sec eb-two">
              <div className="field">
                <label>
                  <span className="eb-label">
                    <span className="eb-n">3</span> Amount
                  </span>
                  <span>{isCall ? `you hold ${fmtNum(balance)} $STRIKE` : `locks ${fmtEth(collateralEth)}`}</span>
                </label>
                <div className="input">
                  <input inputMode="numeric" value={size} onChange={(e) => setSize(e.target.value.replace(/[^\d]/g, ""))} aria-label="Options to sell" />
                  <span className="unit">{isCall ? "$STRIKE" : "options"}</span>
                </div>
                <div className="chips">
                  {isCall && balance > 0
                    ? [0.25, 0.5, 1].map((f) => (
                        <button key={f} onClick={() => setSize(String(Math.floor(balance * f)))}>
                          {f === 1 ? "Max" : `${f * 100}%`}
                        </button>
                      ))
                    : [1_000_000, 5_000_000, 10_000_000].map((x) => (
                        <button key={x} className={n === x ? "chip-on" : ""} onClick={() => setSize(String(x))}>
                          {fmtNum(x)}
                        </button>
                      ))}
                </div>
              </div>
              <div className="field">
                <label>
                  <span className="eb-label">
                    <span className="eb-n">4</span> Your price per 1M
                  </span>
                  <span>vault asks {fmtEth(suggested, 4)}</span>
                </label>
                <div className="input">
                  <input
                    inputMode="decimal"
                    value={pricePerM ?? suggested.toPrecision(3)}
                    onChange={(e) => setPricePerM(e.target.value.replace(/[^\d.]/g, ""))}
                    aria-label="Price per 1M options in ETH"
                  />
                  <span className="unit">ETH</span>
                </div>
                <div className="chips">
                  <button onClick={() => setPricePerM(suggested.toPrecision(3))}>Match vault</button>
                  <button onClick={() => setPricePerM((suggested * 0.95).toPrecision(3))}>−5%, fills faster</button>
                </div>
              </div>
            </div>

            <div className="eb-cta">
              {conn.isConnected ? (
                <button className={`btn btn-lg btn-block ${isCall ? "btn-primary" : "btn-put"}`} disabled={!s || n === 0 || !!busy} onClick={run}>
                  {busy ?? `List ${fmtNum(n)} ${isCall ? "covered calls" : "puts"} at ${strikeLbl}`}
                </button>
              ) : (
                <ConnectButton block />
              )}
              <p className="note">
                Listing is free and your collateral is only locked when a buyer fills the order. Cancel unfilled orders any time in{" "}
                <Link to="/portfolio" className="call">
                  Portfolio
                </Link>
                .
              </p>
            </div>
          </div>

          <aside className={`panel earn-sum tone-${tone}`}>
            <span className="eyebrow">You earn up front</span>
            <span className="es-big num">+{fmtEth(netEth)}</span>
            <span className="mono mute es-usd">≈ {fmtUsd(netEth * eu)}</span>
            <div className="es-chips">
              <span className={`es-chip ${tone}`}>
                {pct(yieldPct)} in {days < 2 ? `${Math.round(days * 24)}h` : `${Math.round(days)}d`}
              </span>
              <span className="es-chip">{pct((yieldPct * 365) / days)} APR</span>
            </div>

            <div className="es-list">
              <div className="kv">
                <span>Collateral</span>
                <span>{isCall ? `${fmtNum(n)} $STRIKE` : fmtEth(collateralEth)}</span>
              </div>
              <div className="kv">
                <span>Premium</span>
                <span>{fmtEth(premiumEth)}</span>
              </div>
              <div className="kv">
                <span>10% protocol fee</span>
                <span className="mute">−{fmtEth(premiumEth * FEE)}</span>
              </div>
              <div className="kv">
                <span>Expires</span>
                <span>{s ? fmtExpiry(s.expiry) : "—"}</span>
              </div>
            </div>

            <span className="eyebrow es-at">At expiry</span>
            <div className="es-case">
              <span className="es-dot ok" />
              <div>
                <strong>
                  If $STRIKE ends {isCall ? "below" : "above"} {strikeLbl}
                </strong>
                <p>{isCall ? `You keep your ${fmtNum(n)} $STRIKE and the premium.` : "Your ETH comes back, plus the premium."}</p>
              </div>
            </div>
            <div className="es-case">
              <span className={`es-dot ${tone}`} />
              <div>
                <strong>
                  If it ends {isCall ? "above" : "below"} {strikeLbl}
                </strong>
                <p>
                  {isCall
                    ? `Your $STRIKE is sold at ${strikeLbl}: you get ${fmtEth(s ? s.strikeEth * n : 0)} plus the premium.`
                    : `You buy ${fmtNum(n)} $STRIKE at ${strikeLbl} with your ETH, and keep the premium.`}
                </p>
              </div>
            </div>
          </aside>
        </div>
      )}
    </section>
  );
}

function Strategy(p: { on: boolean; tone: "call" | "put"; name: string; apr?: number; lock: string; text: string; onClick: () => void }) {
  return (
    <button className={`strat ${p.tone} ${p.on ? "on" : ""}`} onClick={p.onClick} aria-pressed={p.on}>
      <div className="strat-top">
        <span className="strat-name">{p.name}</span>
        {p.apr !== undefined && (
          <span className={`strat-apr num ${p.tone}`}>
            ≈{pct(p.apr)} <span>APR</span>
          </span>
        )}
      </div>
      <p>{p.text}</p>
      <span className="strat-lock mono">{p.lock}</span>
    </button>
  );
}

/** Balances and approvals the writer flow needs, refreshed after each step. */
function useWriterState(cfg: ApiConfig | undefined, me: Address | undefined, isCall: boolean) {
  return useQuery({
    queryKey: ["writer", me, cfg?.contracts.token, isCall],
    enabled: !!cfg && !!me && !!cfg.contracts.token,
    refetchInterval: 15_000,
    queryFn: async () => {
      if (isDemo()) return demoWriterState();
      const c = cfg!.contracts;
      const [strikeBalance, strikeAllowance, wethBalance, wethAllowance, isOperator] = await Promise.all([
        publicClient.readContract({ address: c.token!, abi: erc20Abi, functionName: "balanceOf", args: [me!] }),
        publicClient.readContract({ address: c.token!, abi: erc20Abi, functionName: "allowance", args: [me!, c.options] }),
        publicClient.readContract({ address: c.weth, abi: erc20Abi, functionName: "balanceOf", args: [me!] }),
        publicClient.readContract({ address: c.weth, abi: erc20Abi, functionName: "allowance", args: [me!, c.options] }),
        publicClient.readContract({ address: c.options, abi: strikeOptionsAbi, functionName: "isWriteOperator", args: [me!, c.exchange] }),
      ]);
      return { strikeBalance, strikeAllowance, wethBalance, wethAllowance, isOperator };
    },
  });
}
